import React, { useState, useEffect } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import axios from "axios";
import Loader from "../../Global/Loader";
import { GoAlertFill } from "react-icons/go";

const ChangeMobileNumber = ({ setFormData }) => {
  const [cellPhone, setCellPhone] = useState("+92 ");
  const [isValid, setIsValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const phoneRegex = /^\+92\s\d{10}$/;

  useEffect(() => {
    setIsValid(phoneRegex.test(cellPhone) || cellPhone === "");
  }, [cellPhone]);

  const handleSubmit = async () => {
    if (phoneRegex.test(cellPhone)) {
      try {
        setIsLoading(true);
        const response = await axios.patch(
          `${process.env.REACT_APP_BASE_URL}/auth/change-phone`,
          {
            newPhoneNumber: cellPhone,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response?.data?.success) {
          setSuccess(true);
          setErrorMessage(null);
          setFormData((prev) => ({
            ...prev,
            cellPhone: cellPhone,
          }));
        }
      } catch (error) {
        setErrorMessage(
          error.response?.data?.message ||
            "An unexpected error occurred. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  useGSAP(() => {
    const fields = document.querySelectorAll(".relative");

    fields.forEach((field) => {
      const input = field.querySelector("input, select");
      const label = field.querySelector("label");
      const pLabel = field.querySelector("p"); // Optional error message element

      if (!input || !label) {
        // console.warn("Missing input or label in field:", field);
        return;
      }

      // INITIAL CHECK: If the input is already filled, animate the label to the top.
      if (input.value !== "") {
        gsap.set(label, { top: -10, fontSize: "0.70rem", color: "#6b7280" });
        gsap.set(input, { borderColor: "#6b7280" });
        if (pLabel) {
          pLabel.textContent = `Please enter your ${label.textContent.toLowerCase()}`;
          pLabel.classList.remove("text-red-500");
          pLabel.classList.add("text-gray-500");
        }
      }

      // Focus event listener
      input.addEventListener("focus", () => {
        gsap.to(label, {
          top: -10,
          fontSize: "0.70rem",
          color: "#22c55e",
          duration: 0.25,
        });
        gsap.to(input, { borderColor: "#22c55e", duration: 0.25 });
        if (pLabel) {
          pLabel.classList.remove("text-red-500");
          pLabel.classList.add("text-gray-500");
        }
      });

      // Input event listener
      input.addEventListener("input", () => {
        if (input.value !== "") {
          gsap.to(label, { color: "#22c55e", duration: 0.25 });
          gsap.to(input, { borderColor: "#22c55e", duration: 0.25 });
          if (pLabel) {
            pLabel.classList.remove("text-red-500");
            pLabel.classList.add("text-gray-500");
          }
        }
      });

      // Blur event listener
      input.addEventListener("blur", () => {
        if (input.value === "") {
          gsap.to(label, {
            top: 10,
            fontSize: "1rem",
            color: "#6b7280",
            duration: 0.25,
          });
          gsap.to(input, { borderColor: "#ef4444", duration: 0.25 });
          input.classList.add("error-border");
          if (pLabel) {
            pLabel.textContent = `${label.textContent} Required *`;
            pLabel.classList.add("text-red-500");
          }
        } else {
          gsap.to(label, {
            top: -10,
            fontSize: "0.70rem",
            color: "#6b7280",
            duration: 0.25,
          });
          gsap.to(input, { borderColor: "#6b7280", duration: 0.25 });
          input.classList.remove("error-border");
          if (pLabel) {
            pLabel.textContent = `Please enter your ${label.textContent.toLowerCase()}`;
            pLabel.classList.remove("text-red-500");
            pLabel.classList.add("text-gray-500");
          }
        }
      });
    });
  }, [cellPhone]);

  return (
    <div className="flex flex-col space-y-6 w-[72%]">
      {isLoading && <Loader />}
      {success && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div
            id="successModal"
            className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6"
          >
            {/* Modal Content */}
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Phone Number Updated
            </h2>
            <h4 className="text-sm text-gray-600 mb-6">
              Your phone number has been updated successfully.
            </h4>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setSuccess(false)}
                className="bg-blue-500 text-white px-4 py-2 rounded-md hover:shadow-md focus:outline-none"
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="px-10 py-6 bg-white rounded shadow-lg">
        <h2 className="text-gray-800 text-lg font-semibold mb-4">
          Change Mobile Number
          <span className="text-gray-500 text-base font-thin pl-2">
            change your account Phone Number
          </span>
        </h2>
        <hr className="border-t border-gray-300 mb-4" />
        {errorMessage && (
          <div className="flex items-center gap-x-2 bg-red-100 border border-red-300 text-red-700 text-sm font-semibold px-4 py-4 rounded-md mb-4">
            <GoAlertFill className="size-5" />
            <p>{errorMessage}</p>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols gap-4 mb-6 mt-6">
          <div className="relative">
            <label
              htmlFor="phone"
              className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1"
            >
              Phone Number
            </label>
            <input
              id="phone"
              type="text"
              value={cellPhone}
              onChange={(e) => {
                // Get the new value from the input
                let value = e.target.value;

                // Force the prefix in case it's been removed
                if (!value.startsWith("+92 ")) {
                  value = "+92 ";
                }

                // Remove any non-digit characters from the part after the prefix
                const numericPart = value.slice(4).replace(/\D/g, "");

                // Limit the numeric part to 10 digits
                const limitedNumber = numericPart.slice(0, 10);

                // Update the state with the fixed prefix and the processed digits
                setCellPhone(`+92 ${limitedNumber}`);
              }}
              onKeyDown={(e) => {
                // Allowed control keys
                const allowedKeys = [
                  "Backspace",
                  "ArrowLeft",
                  "ArrowRight",
                  "Delete",
                  "Tab",
                ];
                // Get the cursor position
                const cursorPosition = e.target.selectionStart;

                // Prevent modifications in the prefix area (before index 4)
                if (cursorPosition < 4 && !allowedKeys.includes(e.key)) {
                  e.preventDefault();
                }

                // Allow only digits and control keys
                if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                  e.preventDefault();
                }

                // Prevent input beyond 10 digits after "+92 "
                if (/^\d$/.test(e.key)) {
                  const digitsCount = cellPhone.slice(4).length;
                  const selectionLength = e.target.selectionEnd - e.target.selectionStart;
                  if (digitsCount >= 10 && selectionLength === 0) {
                    e.preventDefault();
                  }
                }
              }}
              className={`w-full p-2 border rounded ${
                !isValid && cellPhone !== "" ? "border-red-500" : ""
              }`}
            />

            <p className={`text-xs mt-1 text-gray-500`}>
              Please enter phone number in correct format e.g: +92 XXX XXXXXXX
            </p>
          </div>
        </div>

        <div className="flex justify-start items-start space-x-4 mt-10 mr-5">
          <button
            onClick={handleSubmit}
            disabled={!isValid || cellPhone === ""}
            className={`px-4 py-2 rounded border ${
              isValid && cellPhone
                ? "bg-white text-blue-500 border-blue-500 hover:bg-blue-500 hover:text-white cursor-pointer"
                : "bg-white text-blue-200 border-blue-200 cursor-not-allowed"
            }`}
          >
            Change Mobile Number
          </button>
          <button
            onClick={() => setCellPhone("+92 ")}
            className="bg-white text-gray-500 border border-gray-500 px-4 py-2 rounded hover:bg-gray-500 hover:text-white"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeMobileNumber;
